// ============================================
// FRANCHISE & CORPORATE STRUCTURE
// Enterprise-grade, franchise-ready model
// ============================================

// Franchise Owner (IP Holder / Group)
export const franchiseOwner = {
  id: 'isf-group',
  name: 'Indian Street Food & Co Group',
  legalName: 'Indian Street Food & Co AS',
  registrationNumber: 'NO 923 456 789 MVA',
  headquarters: {
    address: 'Karl Johans gate 1',
    city: 'Oslo',
    country: 'NO',
    postalCode: '0154',
  },
  foundedYear: 2015,
  founder: 'Dheeraj Singh',
  website: 'https://indianstreetfood.com',
  // Future hooks for franchise management
  franchiseConfig: {
    royaltyPercentage: 5.0,        // % of gross sales
    marketingFeePercentage: 2.0,   // % for brand marketing
    initialFranchiseFee: 500000,   // NOK
    renewalFee: 100000,            // NOK
    contractTermYears: 10,
    territoryProtection: true,
  },
};

// Ownership types for outlets
export const ownershipTypes = {
  CORPORATE: 'corporate',      // Owned by franchise owner
  FRANCHISE: 'franchise',      // Operated by franchisee
  JOINT_VENTURE: 'joint_venture', // Shared ownership
};

// Franchisee Companies
export const franchisees = [
  {
    id: 'isf-corporate',
    type: 'corporate',
    name: 'ISF Corporate Operations',
    legalName: 'Indian Street Food & Co AS',
    registrationNumber: 'NO 923 456 789 MVA',
    country: 'NO',
    contactPerson: 'Erik Andersen',
    email: 'operations@isfco.no',
    phone: '+47 22 12 34 56',
    contractStartDate: '2015-01-01',
    status: 'active',
  },
  {
    id: 'isf-stockholm-ab',
    type: 'franchise',
    name: 'ISF Stockholm AB',
    legalName: 'Indian Street Food Stockholm AB',
    registrationNumber: 'SE 556789-1234',
    country: 'SE',
    contactPerson: 'Anna Lindqvist',
    email: 'anna@isfstockholm.se',
    phone: '+46 8 123 45 67',
    contractStartDate: '2018-03-15',
    contractEndDate: '2028-03-14',
    status: 'active',
    territories: ['Stockholm', 'Uppsala'],
  },
  {
    id: 'isf-solna-ab',
    type: 'franchise',
    name: 'ISF Solna AB',
    legalName: 'Indian Street Food Solna AB',
    registrationNumber: 'SE 556789-5678',
    country: 'SE',
    contactPerson: 'Johan Eriksson',
    email: 'johan@isfsolna.se',
    phone: '+46 8 765 43 21',
    contractStartDate: '2020-06-01',
    contractEndDate: '2030-05-31',
    status: 'active',
    territories: ['Solna', 'Sundbyberg'],
  },
];

// ============================================
// BRAND CONFIGURATION
// ============================================

export const brands = [
  {
    id: 'isf',
    name: 'Indian Street Food & Co',
    shortName: 'ISF&Co',
    logo: '/logo-isf.png',
    primaryColor: '#FF6900',
    secondaryColor: '#32373C',
    cuisine: 'Indian Street Food',
    description: 'Authentic Indian street food experience',
    website: 'https://indianstreetfood.com',
    socialMedia: {
      instagram: '@indianstreetfoodcompany',
      facebook: 'indianstreetfoodcompany',
    },
    isActive: true,
  },
  // Future brand expansion capability
  {
    id: 'bds',
    name: 'By Dheeraj Singh',
    shortName: 'BDS',
    logo: null,
    primaryColor: '#1A1A1A',
    secondaryColor: '#C4A747',
    cuisine: 'Fine Dining Indian',
    description: 'Premium Indian dining experience',
    website: 'https://bydheerajsingh.com',
    isActive: false, // Not yet launched in system
  },
];

// ============================================
// COUNTRY & REGIONAL CONFIGURATION
// ============================================

export const countries = {
  NO: {
    code: 'NO',
    name: 'Norway',
    nameLocal: 'Norge',
    currency: 'NOK',
    currencySymbol: 'kr',
    currencyPosition: 'after', // kr 100
    locale: 'nb-NO',
    timezone: 'Europe/Oslo',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24h',
    vatLabel: 'MVA',
    fiscalYearStart: '01-01',
    regulations: {
      requiresZReport: true,
      requiresReceiptNumber: true,
      maxCashPayment: 40000, // NOK
    },
    isActive: true,
    isPrimary: true,
  },
  SE: {
    code: 'SE',
    name: 'Sweden',
    nameLocal: 'Sverige',
    currency: 'SEK',
    currencySymbol: 'kr',
    currencyPosition: 'after',
    locale: 'sv-SE',
    timezone: 'Europe/Stockholm',
    dateFormat: 'YYYY-MM-DD',
    timeFormat: '24h',
    vatLabel: 'MOMS',
    fiscalYearStart: '01-01',
    regulations: {
      requiresZReport: true,
      requiresReceiptNumber: true,
      maxCashPayment: null,
    },
    isActive: true,
    isPrimary: false,
  },
  // Future expansion
  DK: {
    code: 'DK',
    name: 'Denmark',
    nameLocal: 'Danmark',
    currency: 'DKK',
    currencySymbol: 'kr',
    currencyPosition: 'after',
    locale: 'da-DK',
    timezone: 'Europe/Copenhagen',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24h',
    vatLabel: 'MOMS',
    fiscalYearStart: '01-01',
    isActive: false,
    isPrimary: false,
  },
  GB: {
    code: 'GB',
    name: 'United Kingdom',
    nameLocal: 'United Kingdom',
    currency: 'GBP',
    currencySymbol: '£',
    currencyPosition: 'before',
    locale: 'en-GB',
    timezone: 'Europe/London',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    vatLabel: 'VAT',
    fiscalYearStart: '04-06',
    isActive: false,
    isPrimary: false,
  },
};

// ============================================
// VAT / TAX CONFIGURATION
// Configurable by: Country, Outlet, Order Type, Item Category
// ============================================

export const vatConfig = {
  NO: {
    countryCode: 'NO',
    vatLabel: 'MVA',
    defaultRate: 0.25,
    rates: {
      // By order type and category
      dineIn: {
        food: 0.25,
        beverages: 0.25,
        alcohol: 0.25,
        merchandise: 0.25,
      },
      takeaway: {
        food: 0.15,      // Reduced rate for takeaway food
        beverages: 0.25,
        alcohol: 0.25,
        merchandise: 0.25,
      },
      delivery: {
        food: 0.15,      // Reduced rate for delivery food
        beverages: 0.25,
        alcohol: 0.25,
        merchandise: 0.25,
      },
    },
    // Special categories
    specialRates: {
      accommodationFood: 0.12, // Hotel restaurant food
      culturalEvents: 0.12,
      publicTransport: 0.12,
    },
    exemptions: ['export', 'intraEU'],
    reverseChargeThreshold: null,
    registrationThreshold: 50000, // NOK
  },
  SE: {
    countryCode: 'SE',
    vatLabel: 'MOMS',
    defaultRate: 0.25,
    rates: {
      dineIn: {
        food: 0.12,      // 12% for food
        beverages: 0.12,
        alcohol: 0.25,   // 25% for alcohol
        merchandise: 0.25,
      },
      takeaway: {
        food: 0.12,
        beverages: 0.12,
        alcohol: 0.25,
        merchandise: 0.25,
      },
      delivery: {
        food: 0.12,
        beverages: 0.12,
        alcohol: 0.25,
        merchandise: 0.25,
      },
    },
    specialRates: {
      books: 0.06,
      newspapers: 0.06,
      culturalEvents: 0.06,
    },
    exemptions: ['export', 'intraEU'],
    reverseChargeThreshold: null,
    registrationThreshold: 30000, // SEK
  },
};

// ============================================
// MULTI-CURRENCY SUPPORT
// ============================================

export const currencies = {
  NOK: {
    code: 'NOK',
    name: 'Norwegian Krone',
    symbol: 'kr',
    position: 'after',
    decimals: 0, // Norway rounds to whole kroner
    thousandSeparator: ' ',
    decimalSeparator: ',',
    isBaseCurrency: true,
    exchangeRate: 1.0,
  },
  SEK: {
    code: 'SEK',
    name: 'Swedish Krona',
    symbol: 'kr',
    position: 'after',
    decimals: 0,
    thousandSeparator: ' ',
    decimalSeparator: ',',
    isBaseCurrency: false,
    exchangeRate: 0.98, // 1 SEK = 0.98 NOK
  },
  EUR: {
    code: 'EUR',
    name: 'Euro',
    symbol: '€',
    position: 'before',
    decimals: 2,
    thousandSeparator: '.',
    decimalSeparator: ',',
    isBaseCurrency: false,
    exchangeRate: 11.50, // 1 EUR = 11.50 NOK
  },
  USD: {
    code: 'USD',
    name: 'US Dollar',
    symbol: '$',
    position: 'before',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    isBaseCurrency: false,
    exchangeRate: 10.80, // 1 USD = 10.80 NOK
  },
  GBP: {
    code: 'GBP',
    name: 'British Pound',
    symbol: '£',
    position: 'before',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    isBaseCurrency: false,
    exchangeRate: 13.50, // 1 GBP = 13.50 NOK
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

export const formatCurrency = (amount, currencyCode = 'NOK', options = {}) => {
  const currency = currencies[currencyCode];
  if (!currency) return `${amount}`;
  
  const { showSymbol = true, showCode = false } = options;
  
  const formattedAmount = amount.toLocaleString(
    currencyCode === 'NOK' ? 'nb-NO' : 'sv-SE',
    {
      minimumFractionDigits: currency.decimals,
      maximumFractionDigits: currency.decimals,
    }
  );
  
  if (!showSymbol && !showCode) return formattedAmount;
  
  const symbol = showCode ? currencyCode : currency.symbol;
  
  return currency.position === 'before'
    ? `${symbol}${formattedAmount}`
    : `${symbol} ${formattedAmount}`;
};

export const getVatRate = (countryCode, orderType, category) => {
  const config = vatConfig[countryCode];
  if (!config) return 0.25; // Default fallback
  
  return config.rates[orderType]?.[category] ?? config.defaultRate;
};

export const calculateVat = (netAmount, countryCode, orderType, category) => {
  const rate = getVatRate(countryCode, orderType, category);
  const vatAmount = netAmount * rate;
  return {
    netAmount,
    vatRate: rate,
    vatAmount: Math.round(vatAmount),
    grossAmount: Math.round(netAmount + vatAmount),
  };
};

export const getFranchiseeById = (id) => franchisees.find(f => f.id === id);
export const getFranchiseesByCountry = (country) => franchisees.filter(f => f.country === country);
export const getCountryConfig = (code) => countries[code];
export const getBrandById = (id) => brands.find(b => b.id === id);
